/**
 * Müşteri Performans Raporu — dönem tanımları ve ortak sabitler.
 *
 * Dört ayrı rapor sayfası tek sayfada birleştirilirken (bkz.
 * `FirmPerformanceUnifiedReportPage`) dönemlere ait metin/izin/uç nokta
 * eşlemesi sayfanın içinde kalabilirdi; ayrı dosyada durması iki işe yarıyor:
 * eski rota yönlendirmeleri de aynı dönem anahtarlarını kullanıyor ve dönem
 * eklemek sayfayı okumadan mümkün oluyor.
 */

export const PERIOD_DAY = "day";
export const PERIOD_MONTH = "month";
export const PERIOD_RANGE = "range";
export const PERIOD_YEAR = "year";

/**
 * Dönem listesi (#338, sözleşme firm-performance-report.md). `permission`
 * SUNUCUDAKİ dört dönem izniyle birebir aynıdır; kullanıcı hangi izne
 * sahipse yalnızca o dönemi görür. Günlük / Aylık / Yıllık'ta "Belirli" ya
 * da "Son N" seçilir (`lastN`: N seçenek aralığı ve birim adı).
 */
export const PERIOD_OPTIONS = [
  {
    id: PERIOD_DAY,
    value: PERIOD_DAY,
    label: "Günlük",
    permission: "reports.firmDailyPerformance",
    lastN: { min: 2, max: 31, unit: "gün" },
    specificLabel: "Belirli Gün",
    description:
      "Belirli bir gün (saat saat) ya da son N gün; tepe firma / bayi seçilirse alt müşteriler de dahil",
    requiredMessage: "Müşteri, ürün ve tarih seçimi zorunludur",
    emptyMessage: "Müşteriyi, ürünü ve günü seçip 'Raporu Getir' butonuna basın.",
  },
  {
    id: PERIOD_MONTH,
    value: PERIOD_MONTH,
    label: "Aylık",
    permission: "reports.firmPerformance",
    lastN: { min: 2, max: 6, unit: "ay" }, // en çok son 6 ay (kullanıcı kararı; sunucu da 400)
    specificLabel: "Belirli Ay",
    description:
      "Belirli bir ay ya da son N ay; tepe firma / bayi seçilirse alt müşteriler de dahil",
    requiredMessage: "Müşteri, ürün ve ay seçimi zorunludur",
    emptyMessage: "Müşteriyi, ürünü ve ayı seçip 'Raporu Getir' butonuna basın.",
  },
  {
    id: PERIOD_YEAR,
    value: PERIOD_YEAR,
    label: "Yıllık",
    permission: "reports.firmsPerformance",
    lastN: { min: 2, max: 5, unit: "yıl" },
    specificLabel: "Belirli Yıl",
    description:
      "Belirli bir yıl ya da son N yıl, makine × ay; tepe firma / bayi seçilirse alt müşteriler de dahil",
    requiredMessage: "Müşteri, ürün ve yıl seçimi zorunludur",
    emptyMessage: "Müşteriyi, ürünü ve yılı seçip 'Raporu Getir' butonuna basın.",
  },
  {
    id: PERIOD_RANGE,
    value: PERIOD_RANGE,
    label: "Tarih Aralığı",
    permission: "reports.firmRangePerformance",
    description:
      "Başlangıç ve bitiş tarihini kendiniz verin; 45 günü aşan aralıkta tablo ay ay",
    requiredMessage: "Müşteri, ürün ve tarih aralığı seçimi zorunludur",
    emptyMessage:
      "Müşteriyi, ürünü ve tarih aralığını seçip 'Raporu Getir' butonuna basın.",
  },
];

export const MODE_SPECIFIC = "specific";
export const MODE_LAST_N = "lastN";

/** Sunucunun `period` değeri (range-performance izni / matris sütun birimi). */
const SERVER_PERIOD = {
  [PERIOD_DAY]: "daily",
  [PERIOD_MONTH]: "monthly",
  [PERIOD_YEAR]: "yearly",
  [PERIOD_RANGE]: "range",
};

/**
 * Seçimden hangi uca ne gönderileceği (#338). Saf fonksiyon — tarih
 * hesapları test edilebilsin.
 *
 * - Aylık · belirli ay → performance
 * - diğer hepsi → range-performance + `period` — Günlük · belirli gün de
 *   (başlangıç = bitiş): 1 gün ve son N gün aynı şablonu kullanır (#338)
 *
 * @returns {{ kind: "monthly"|"range", body: object, label: string }}
 */
export const buildPerformanceRequest = ({
  period,
  mode,
  n,
  date,
  year,
  month,
  startDate,
  endDate,
  today = new Date(),
}) => {
  const ty = today.getFullYear();
  const tm = today.getMonth();
  const range = (start, end, label) => ({
    kind: "range",
    body: { startDate: toIsoDay(start), endDate: toIsoDay(end), period: SERVER_PERIOD[period] },
    label,
  });

  if (period === PERIOD_RANGE) {
    return range(
      startDate,
      endDate,
      `${new Date(startDate).toLocaleDateString("tr-TR")} – ${new Date(endDate).toLocaleDateString("tr-TR")}`,
    );
  }
  if (mode === MODE_LAST_N) {
    if (period === PERIOD_DAY) {
      return range(new Date(ty, tm, today.getDate() - (n - 1)), today, `Son ${n} gün`);
    }
    if (period === PERIOD_MONTH) {
      return range(new Date(ty, tm - (n - 1), 1), today, `Son ${n} ay`);
    }
    return range(new Date(ty - (n - 1), 0, 1), today, `Son ${n} yıl`);
  }
  if (period === PERIOD_DAY) {
    return range(date, date, new Date(date).toLocaleDateString("tr-TR"));
  }
  if (period === PERIOD_MONTH) {
    return { kind: "monthly", body: { year, month }, label: `${MONTH_NAMES[month - 1]} ${year}` };
  }
  // Belirli yıl: cari yılda bugüne kadar
  return range(new Date(year, 0, 1), year === ty ? today : new Date(year, 11, 31), String(year));
};

/** Diğer rapor sayfalarındaki statik ürün listesiyle aynı. */
export const PRODUCT_OPTIONS = [
  { id: "Çay", label: "Çay", value: "Çay" },
  { id: "Türk Kahvesi", label: "Türk Kahvesi", value: "Türk Kahvesi" },
  { id: "Filtre Kahve", label: "Filtre Kahve", value: "Filtre Kahve" },
  // Espresso = 3 temel ürün dışındaki TÜM türevler (Ristretto, Cappuccino,
  // Mocha…). Sunucu bunu kural olarak çözer ve PDF'e türev kırılımını ekler.
  { id: "Espresso", label: "Espresso", value: "Espresso" },
];

export const MONTH_NAMES = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

/** Platform verisinin başladığı yıl — daha eskisini seçmenin anlamı yok. */
export const FIRST_YEAR = 2022;

/** Date -> "YYYY-AA-GG" (yerel gün; toISOString UTC'ye kaydırıp günü bozar). */
export const toIsoDay = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
